import { getAuthHeaders } from '../utils/authHeaders';
import { canonicaliseOwner } from '../utils/customerAliases';

const API_URL = 'https://app.pvslimited.co.uk/api';

const FORM_SEQUENCE = [
  'preInspection',
  'paintQualityInspection',
  'tankCertification',
  'finalQCChecklist'
];

const FORM_LABELS = {
  preInspection: 'Pre Inspection Form',
  paintQualityInspection: 'Paint Quality Inspection Form',
  tankCertification: 'Tank Certification Form',
  finalQCChecklist: 'Final QC Checklist Form'
};

const SCRAP_STATUS = 'Scrap';

export const isVesselScrapped = (vessel) => vessel?.refurbStatus === SCRAP_STATUS;

export const getScrappedFormMessage = (formType) => (
  `This vessel has been scrapped. A ${FORM_LABELS[formType] || 'form'} cannot be submitted against a scrapped vessel.`
);

const getSequenceBlockerMessage = (formProgress, formType, examinationType = '') => {
  const targetIndex = FORM_SEQUENCE.indexOf(formType);
  if (targetIndex <= 0) return '';

  // In-Service tank certifications can skip prior forms.
  if (
    formType === 'tankCertification' &&
    String(examinationType || '').toLowerCase() === 'inservice'
  ) {
    return '';
  }

  for (let i = 0; i < targetIndex; i += 1) {
    const prereq = FORM_SEQUENCE[i];
    if (!formProgress?.[prereq]?.completed) {
      return `Cannot complete ${FORM_LABELS[formType]} before ${FORM_LABELS[prereq]}.`;
    }
  }

  return '';
};

export const validateFormCompletion = async (serialNumber, formType, extra = {}) => {
  const trimmed = String(serialNumber || '').trim();
  if (!trimmed) {
    throw new Error('Serial number is required.');
  }

  let response;
  try {
    response = await fetch(`${API_URL}/validate-serial`, {
      method: 'POST',
      headers: { ...getAuthHeaders(true) },
      body: JSON.stringify({ serialNumber: trimmed })
    });
  } catch (networkError) {
    throw new Error('Unable to validate serial number — network error. Please check your connection and try again.');
  }

  let data;
  try {
    data = await response.json();
  } catch (parseError) {
    throw new Error('Vessel Serial Number not found in Vessel Inventory.');
  }

  if (!response.ok || !data.valid) {
    throw new Error(data.message || 'Vessel Serial Number not found in Vessel Inventory.');
  }

  if (FORM_SEQUENCE.includes(formType)) {
    if (isVesselScrapped(data.vessel)) {
      throw new Error(getScrappedFormMessage(formType));
    }

    const blocker = getSequenceBlockerMessage(
      data.formProgress || {},
      formType,
      extra.examinationType
    );
    if (blocker) {
      throw new Error(blocker);
    }
  }

  return data;
};

export const lookupVesselBySerial = async (serialNumber) => {
  const trimmed = String(serialNumber || '').trim();
  if (!trimmed) return { found: false, vessel: null };

  try {
    const response = await fetch(`${API_URL}/validate-serial`, {
      method: 'POST',
      headers: { ...getAuthHeaders(true) },
      body: JSON.stringify({ serialNumber: trimmed })
    });

    let data = {};
    try {
      data = await response.json();
    } catch (_) {
      // non-JSON body
    }

    if (!response.ok || !data.valid) {
      return { found: false, vessel: null };
    }

    const vessel = data.vessel
      ? { ...data.vessel, owner: canonicaliseOwner(data.vessel.owner) }
      : null;
    return { found: true, vessel, formProgress: data.formProgress || {} };
  } catch (error) {
    console.warn('Vessel lookup failed:', error.message);
    return { found: false, vessel: null, error: error.message };
  }
};

// For forms that may be submitted for vessels not held in inventory (e.g.
// In-Service tank certifications): only a known, scrapped vessel is blocked.
export const assertVesselNotScrapped = async (serialNumber, formType) => {
  const { found, vessel } = await lookupVesselBySerial(serialNumber);
  if (found && isVesselScrapped(vessel)) {
    throw new Error(getScrappedFormMessage(formType));
  }
};

export const markFormCompleted = async (serialNumber, formType, completedAt, extra = {}) => {
  const trimmed = String(serialNumber || '').trim();
  if (!trimmed || !formType) {
    return { success: false };
  }

  try {
    const response = await fetch(`${API_URL}/mark-form-complete`, {
      method: 'POST',
      headers: { ...getAuthHeaders(true) },
      body: JSON.stringify({
        serialNumber: trimmed,
        formType,
        completedAt: completedAt || new Date().toISOString(),
        examinationType: extra.examinationType || '',
        completedBy: extra.completedBy || ''
      })
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || 'Failed to update form progress.');
    }
    return data;
  } catch (error) {
    throw error;
  }
};

export const markVesselScrap = async (serialNumber) => {
  const trimmed = String(serialNumber || '').trim();
  if (!trimmed) return { success: false };

  try {
    const response = await fetch(`${API_URL}/mark-vessel-scrap`, {
      method: 'POST',
      headers: { ...getAuthHeaders(true) },
      body: JSON.stringify({ serialNumber: trimmed })
    });
    const data = await response.json();
    if (!response.ok) {
      console.warn('Mark vessel scrap failed:', data.message);
    }
    return data;
  } catch (error) {
    console.warn('Mark vessel scrap failed (network):', error.message);
    return { success: false };
  }
};
